import React from 'react';
import {Box, Typography} from '@mui/material';
import {FAMILY_NAME_BY_CODE, FAMILY_DISPLAY_ORDER, familyOfActionCode} from './priorityFamilies';

/**
 * The two cells every Priority Actions table shares — School slides 34/36,
 * Network slides 33/35 (cards S_20/S_21 and S_14/S_15 C_2).
 *
 * BASELINE EVIDENCE ("Areas of Weakness" on the School slides) arrives as
 * separate lists (datahouse, 1 Oct 2026):
 *     { readiness: [...], cyber_risk: [...], context: [...] }
 * drawn as the slides draw it — a bold group name, its items as bullets. A
 * group with no items is left out. A plain string (the older format) is
 * shown as given.
 *
 * RECOMMENDATIONS are one string, items separated by ";" ("SS1; SS3", or
 * "Cyberbullying Management: Effective Handling…; Maintaining…"). The cell's
 * data carries each item's name (labels) and code (codes, aligned) — when every
 * code names a standard action family (its two-letter prefix: PE2 → Parental
 * Engagement), the bullets are grouped under the family names in the order of
 * the "Standard Action Families" row, like the evidence cell groups its items
 * (QA, 9 Oct 2026). Otherwise the names are one flat list; without names the
 * string is split on ";".
 */

/** Group names as the slides print them, in the slides' order. */
const EVIDENCE_GROUPS = [
    ['readiness', 'Child Online Safety Readiness'],
    ['cyber_risk', 'Cyber-Risk Experiences'],
    ['context', 'Context & Support'],
];

const isGroupedEvidence = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

const BulletList = ({items}) => (
    <Box component="ul" sx={{m: 0, pl: 2.25}}>
        {items.map((item, i) => (
            <Typography component="li" key={i} sx={{fontSize: 'inherit', lineHeight: 1.5}}>{item}</Typography>
        ))}
    </Box>
);

/** @param {*} value the row's raw Baseline Evidence value (row._raw.evidence) */
export const EvidenceCell = ({value, fallback = '—'}) => {
    if (!isGroupedEvidence(value)) return <>{fallback}</>;
    const groups = EVIDENCE_GROUPS
        .map(([key, label]) => [label, (Array.isArray(value[key]) ? value[key] : []).filter(Boolean)])
        .filter(([, items]) => items.length);
    if (!groups.length) return <>—</>;
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
            {groups.map(([label, items]) => (
                <Box key={label}>
                    <Typography sx={{fontSize: 'inherit', fontWeight: 700}}>{label}</Typography>
                    <BulletList items={items}/>
                </Box>
            ))}
        </Box>
    );
};

/** @param {object} row a rowsFrom row whose `recommendation` key holds the column */
export const RecommendationCell = ({row}) => {
    const cell = row._cell?.recommendation;
    const labels = Array.isArray(cell?.labels) && cell.labels.length ? cell.labels : null;
    const codes = labels && Array.isArray(cell?.codes) && cell.codes.length === labels.length ? cell.codes : null;
    const families = codes ? codes.map(familyOfActionCode) : null;
    if (families && families.every(Boolean)) {
        const byFamily = new Map();
        families.forEach((fam, i) => {
            if (!byFamily.has(fam)) byFamily.set(fam, []);
            if (!byFamily.get(fam).includes(labels[i])) byFamily.get(fam).push(labels[i]);
        });
        const order = (fam) => { const i = FAMILY_DISPLAY_ORDER.indexOf(fam); return i < 0 ? FAMILY_DISPLAY_ORDER.length : i; };
        const groups = [...byFamily.entries()].sort((a, b) => order(a[0]) - order(b[0]));
        return (
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                {groups.map(([fam, items]) => (
                    <Box key={fam}>
                        <Typography sx={{fontSize: 'inherit', fontWeight: 700}}>{FAMILY_NAME_BY_CODE[fam]}</Typography>
                        <BulletList items={items}/>
                    </Box>
                ))}
            </Box>
        );
    }
    const items = labels || String(row._raw?.recommendation ?? '').split(';').map((s) => s.trim()).filter(Boolean);
    return items.length ? <BulletList items={items}/> : <>{row.recommendation}</>;
};
